import { InternalSystemType } from 'Api/Enums/InternalSystemType';

export type JsonType =
    | 'string'
    | 'null'
    | 'boolean'
    | 'array'
    | 'object'
    | 'integer'
    | 'number';

export type JsonFormat =
    | 'date-time'
    | 'time'
    | 'date'
    | 'duration'
    | 'hostname'
    | 'email'
    | 'ipv4'
    | 'ipv6'
    | 'uuid'
    | 'uri'
    | 'regex';

export type ContentEncoding =
    | 'quoted-printable'
    | 'base16'
    | 'base32'
    | 'base64';

export type JsonValue =
    | string
    | number
    | boolean
    | null
    | JsonObject
    | JsonArray;

export type JsonObject = {
    [key: string]: JsonValue;
};

export type JsonArray = Array<JsonValue>;

export type JsonSchema = {
    $id?: string;
    $schema?: string;
    $anchor?: string;
    $dynamicAnchor?: string;
    $ref?: string;
    $dynamicRef?: string;
    $defs?: Record<string, JsonSchema>;
    $comment?: string;
    title?: string;
    description?: string;
    type?: JsonType | Array<JsonType>;
    properties?: Record<string, JsonSchema>;
    default?: JsonValue;
    deprecated?: boolean;
    readOnly?: boolean;
    writeOnly?: boolean;
    enum?: Array<JsonValue>;
    const?: JsonValue;
    multipleOf?: number;
    maximum?: number;
    exclusiveMaximum?: number;
    minimum?: number;
    exclusiveMinimum?: number;
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    maxItems?: number;
    minItems?: number;
    uniqueItems?: boolean;
    maxContains?: number;
    minContains?: number;
    maxProperties?: number;
    minProperties?: number;
    required?: Array<string>;
    dependentRequired?: Record<string, Array<string>>;
    prefixItems?: Array<JsonSchema>;
    items?: JsonSchema;
    contains?: JsonSchema;
    patternProperties?: Record<string, JsonSchema>;
    additionalProperties?: JsonSchema;
    propertyNames?: JsonSchema;
    unevaluatedItems?: JsonSchema;
    unevaluatedProperties?: JsonSchema;
    allOf?: Array<JsonSchema>;
    anyOf?: Array<JsonSchema>;
    oneOf?: Array<JsonSchema>;
    not?: JsonSchema;
    if?: JsonSchema;
    then?: JsonSchema;
    else?: JsonSchema;
    dependentSchemas?: Record<string, JsonSchema>;
    format?: JsonFormat;
    contentMediaType?: string;
    contentEncoding?: ContentEncoding;
};

export type JsonSchemaDto = {
    jsonSchemaId: number;
    name: string;
    description?: string;
    schema: JsonSchema;
    visibility: InternalSystemType;
}

export function createObjectFromSchema(jsonSchema: JsonSchema): JsonObject {
    return jsonSchema?.type == 'object'
        ? createObjectFromSchemaInternal(jsonSchema) as JsonObject
        : null;
}

function createObjectFromSchemaInternal(jsonSchema: JsonSchema, rootJsonSchema?: JsonSchema): JsonValue {
    if ('default' in jsonSchema) {
        return jsonSchema.default;
    }

    const [jsonType, jsonRef] = getType(jsonSchema, rootJsonSchema);

    switch (jsonType) {
        case 'null':
            return null;
        case 'string':
            return '';
        case 'number':
        case 'integer':
            return 0;
        case 'boolean':
            return false;
        case 'array':
            return [];
        case 'object': {
            const currentSchema = jsonRef ?? jsonSchema;
            const obj: JsonObject = {};
            const schemaProperties: Record<string, JsonSchema> = currentSchema.properties ?? {};
            rootJsonSchema ??= jsonSchema;

            for (const [key, value] of Object.entries(schemaProperties)) {
                obj[key] = createObjectFromSchemaInternal(value, rootJsonSchema);
            }

            return obj;
        }
        default:
            throw new Error('not supported type');
    }
}

function getType(jsonSchema: JsonSchema, rootJsonSchema?: JsonSchema): [JsonType, JsonSchema?] {
    if (jsonSchema.$ref) {
        const $defs: JsonSchema = getRef(jsonSchema.$ref, rootJsonSchema);
        const [subType] = getType($defs, rootJsonSchema);

        return [subType, $defs];
    }

    if (!('type' in jsonSchema)) {
        throw new Error('type should be defined');
    }

    if (!Array.isArray(jsonSchema.type)) {
        return [jsonSchema.type, null];
    }

    if (jsonSchema.type.some(t => t == 'null')) {
        return ['null'];
    }

    return [jsonSchema.type.find(t => t != 'null')];
}

function getRef(refPath: string, rootJsonSchema: JsonSchema): JsonSchema {
    if (refPath.startsWith('#/$defs/')) {
        return refPath
            .slice(2)
            .split('/')
            .filter(segment => segment != '')
            .reduce(
                (current: Record<string, any>, segment: string) => (
                    current && typeof current === 'object' && segment in current
                        ? current[segment]
                        : null),
                rootJsonSchema);
    }
    else if (refPath.startsWith('/')) {
        throw new Error('$ref not implemented');
    }
    else if (refPath.startsWith('http')) {
        throw new Error('$ref http not implemented');
    }
    else {
        throw new Error('$ref not supported');
    }
}
